import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  model,
  output,
  signal,
} from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatchType, Targeting } from "@front/m19-api-client";
import { CheckKeywordTargetingResult, Constant, SegmentService } from "@front/m19-services";
import {
  ClipboardService,
  IBadgeComponent,
  IButtonDirective,
  ICheckboxComponent,
  IInputGroupDirective,
  IInputGroupInput,
  IInputGroupSuffixDirective,
  ISwitchButtonComponent,
  ModalService,
  IIconComponent,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { CsvExportService, simpleField } from "@m19-board/services/csv-export.service";
import {
  KeywordListManagerReportModalComponent,
  KeywordListManagerReportModalData,
} from "@m19-board/shared/keyword-list-manager/keyword-list-manager-report-modal.component";
import { filter } from "rxjs";

@Component({
  selector: "app-keyword-list-manager",
  imports: [
    CommonModule,
    TranslocoDirective,
    ICheckboxComponent,
    IButtonDirective,
    IBadgeComponent,
    IInputGroupDirective,
    IInputGroupInput,
    FormsModule,
    ReactiveFormsModule,
    IInputGroupSuffixDirective,
    ISwitchButtonComponent,
    IIconComponent,
  ],
  templateUrl: "./keyword-list-manager.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeywordListManagerComponent {
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly csvExportService = inject(CsvExportService);
  private readonly clipboard = inject(ClipboardService);

  initTargetings = input<Targeting[], Targeting[]>([], {
    transform: (targetings: Targeting[]) => {
      this.keywords.set(targetings);
      return targetings;
    },
  });
  importedTargetings = input<Targeting[], Targeting[]>([], {
    transform: (targetings: Targeting[]) => {
      const validationResult = this.validateTargetingsList(targetings);
      if (validationResult.errors.length) {
        this.openReportModal(validationResult);
      } else {
        this.keywords.set(targetings);
      }
      return targetings;
    },
  });

  isReadOnly = input<boolean>(false);
  csvPrefix = input<string>("");
  targetingMode = input<boolean>(true);
  addInputPlaceholder = model<string>(
    this.translocoService.translate("keyword-segment-modal.keyword_input_placeholder", [
      SegmentService.MATCH_TYPE_EXACT_MAX_WORDS,
    ]),
  );
  maxWordPerKeyword = model<number | undefined>(undefined);
  maxKeywords = input<number>(Constant.maxKeywordBySegment);
  defaultMatchType = input<MatchType>(MatchType.exact);
  targetingsChanged = output<Targeting[]>();

  readonly MatchType = MatchType;

  matchType = linkedSignal<MatchType>(() => this.defaultMatchType());
  lastChecked: Targeting | null = null;
  filter = signal<string>("");
  keywordInput = new FormControl("");
  keywords = signal<Targeting[]>([]);
  keywordChecked = signal<Set<Targeting>>(new Set());
  isSearchBarVisible = signal(false);
  isAddKeywordVisible = signal(false);
  filteredKeywords = computed(() => {
    return this.keywords().filter((k) => k.targetingValue.toLowerCase().includes(this.filter().toLowerCase()));
  });
  isAllSelected = computed(
    () => this.filteredKeywords().length > 0 && this.filteredKeywords().every((k) => this.keywordChecked().has(k)),
  );

  addKeywordFromInput(event?: Event) {
    event?.preventDefault();

    if (!this.keywordInput.value) return;
    const keyword = this.keywordInput.value;
    const keywordCheckResult = this.validateKeywordsList([keyword]);

    if (keywordCheckResult.errors.length > 0) {
      this.openReportModal(keywordCheckResult);
    } else {
      this.keywords.update((keywords) => [...keywords, ...keywordCheckResult.addedKeywords]);
      this.keywordInput.setValue("");
      this.emitKeywords();
    }
  }

  handleKeywordClick(event: Event, keyword: Targeting) {
    if (event instanceof MouseEvent && event.shiftKey && this.lastChecked) {
      const start = this.keywords().indexOf(this.lastChecked);
      const end = this.keywords().indexOf(keyword);

      const min = Math.min(start, end);
      const max = Math.max(start, end);

      this.keywords()
        .slice(min, max + 1)
        .forEach((k) => {
          if (k !== this.lastChecked) {
            this.toggleKeyword(k);
          }
        });
    } else {
      this.toggleKeyword(keyword);
    }
    this.lastChecked = keyword;
  }

  handlePaste(text: string) {
    if (text) {
      const keywords = text.trim().split(/\n+/);
      const keywordCheckResults = this.validateKeywordsList(keywords);

      this.openReportModal(keywordCheckResults);
    }
  }

  addKeywords(keywords: string[]) {
    this.keywords.update((kws) => [
      ...kws,
      ...keywords.map((k) => ({ targetingValue: k, matchType: this.matchType() })),
    ]);
    this.emitKeywords();
  }

  deleteCheckedKeywords() {
    this.keywords.update((kws) => kws.filter((k) => !this.keywordChecked().has(k)));
    this.keywordChecked.set(new Set());
    this.emitKeywords();
  }

  setMatchType(matchType: MatchType) {
    this.matchType.set(matchType);
    this.addInputPlaceholder.set(
      this.translocoService.translate("keyword-segment-modal.keyword_input_placeholder", [
        matchType === MatchType.exact
          ? SegmentService.MATCH_TYPE_EXACT_MAX_WORDS
          : SegmentService.MATCH_TYPE_PHRASE_MAX_WORDS,
      ]),
    );
  }

  selectAllKeywords() {
    this.keywordChecked.update((set) => {
      if (this.isAllSelected()) {
        this.filteredKeywords().forEach((k) => set.delete(k));
      } else {
        this.filteredKeywords().forEach((k) => set.add(k));
      }
      return new Set([...set]);
    });
  }

  exportKeywordsAsCsv() {
    this.csvExportService.exportCsv(
      {
        prefix: this.csvPrefix() + "_keywords",
      },
      this.keywords(),
      [simpleField("matchType"), simpleField("targetingValue", "keyword")],
    );
  }

  copyKeyword(keyword: Targeting) {
    this.clipboard.copyToClipboard(keyword.targetingValue);
  }

  private toggleKeyword(keyword: Targeting) {
    this.keywordChecked.update((set) => {
      if (set.has(keyword)) {
        set.delete(keyword);
      } else {
        set.add(keyword);
      }
      return new Set([...set]);
    });
  }

  private openReportModal(keywordCheckResults: CheckKeywordTargetingResult) {
    this.modalService
      .openModal<KeywordListManagerReportModalData, boolean>(KeywordListManagerReportModalComponent, {
        size: "md",
        modalTitle: this.translocoService.translate("keyword-segment-modal.keyword_results"),
        data: {
          keywordCheckResults,
        },
      })
      .afterClosed()
      .pipe(filter((confirm) => confirm))
      .subscribe(() => {
        this.addKeywords(keywordCheckResults.addedKeywords.map((k) => k.targetingValue));
      });
  }

  private validateKeywordsList(keywords: string[]): CheckKeywordTargetingResult {
    const targetings: Targeting[] = keywords.map((k) => ({ targetingValue: k, matchType: this.matchType() }));
    return this.validateTargetingsList(targetings);
  }

  private validateTargetingsList(targetings: Targeting[]): CheckKeywordTargetingResult {
    return SegmentService.checkTargetingKeywords(
      targetings,
      this.keywords(),
      this.maxKeywords(),
      this.maxWordPerKeyword(),
    );
  }

  private emitKeywords() {
    this.targetingsChanged.emit(this.keywords());
  }
}
